import { __assign } from "tslib";
import isPropValid from '@emotion/is-prop-valid';
import React, { createElement, useDebugValue } from 'react';
import { SC_VERSION } from '../constants';
import { checkDynamicCreation } from '../utils/checkDynamicCreation';
import createWarnTooManyClasses from '../utils/createWarnTooManyClasses';
import determineTheme from '../utils/determineTheme';
import domElements from '../utils/domElements';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../utils/empties';
import escape from '../utils/escape';
import generateComponentId from '../utils/generateComponentId';
import generateDisplayName from '../utils/generateDisplayName';
import hoist from '../utils/hoist';
import isFunction from '../utils/isFunction';
import isStyledComponent from '../utils/isStyledComponent';
import isTag from '../utils/isTag';
import { joinStrings } from '../utils/joinStrings';
import merge from '../utils/mixinDeep';
import { setToString } from '../utils/setToString';
import ComponentStyle from './ComponentStyle';
import { useStyleSheetContext } from './StyleSheetManager';
import { ThemeContext } from './ThemeProvider';
var identifiers = {};
/* We depend on components having unique IDs */
function generateId(displayName, parentComponentId) {
    var name = typeof displayName !== 'string' ? 'sc' : escape(displayName);
    // Ensure that no displayName can lead to duplicate componentIds
    identifiers[name] = (identifiers[name] || 0) + 1;
    var componentId = "".concat(name, "-").concat(generateComponentId(
    // SC_VERSION gives us isolation between multiple runtimes on the page at once
    // this is improved further with use of the babel plugin "namespace" feature
    SC_VERSION + name + identifiers[name]));
    return parentComponentId ? "".concat(parentComponentId, "-").concat(componentId) : componentId;
}
function useInjectedStyle(componentStyle, resolvedAttrs) {
    var ssc = useStyleSheetContext();
    var className = componentStyle.generateAndInjectStyles(resolvedAttrs, ssc.styleSheet, ssc.stylis);
    if (process.env.NODE_ENV !== 'production')
        useDebugValue(className);
    return className;
}
function resolveContext(attrs, props, theme) {
    var context = __assign(__assign({}, props), { 
        // unset, add `props.className` back at the end so props always "wins"
        className: undefined, theme: theme });
    var attrDef;
    for (var i = 0; i < attrs.length; i += 1) {
        attrDef = attrs[i];
        var resolvedAttrDef = isFunction(attrDef) ? attrDef(context) : attrDef;
        for (var key in resolvedAttrDef) {
            context[key] =
                key === 'className'
                    ? joinStrings(context[key], resolvedAttrDef[key])
                    : key === 'style'
                        ? __assign(__assign({}, context[key]), resolvedAttrDef[key]) : resolvedAttrDef[key];
        }
    }
    if (props.className) {
        context.className = joinStrings(context.className, props.className);
    }
    return context;
}
var seenUnknownProps = new Set();
function useStyledComponentImpl(forwardedComponent, props, forwardedRef) {
    var componentAttrs = forwardedComponent.attrs, componentStyle = forwardedComponent.componentStyle, defaultProps = forwardedComponent.defaultProps, foldedComponentIds = forwardedComponent.foldedComponentIds, styledComponentId = forwardedComponent.styledComponentId, target = forwardedComponent.target;
    var contextTheme = React.useContext(ThemeContext);
    var ssc = useStyleSheetContext();
    var shouldForwardProp = forwardedComponent.shouldForwardProp || ssc.shouldForwardProp;
    if (process.env.NODE_ENV !== 'production')
        useDebugValue(styledComponentId);
    // NOTE: the non-hooks version only subscribes to this when !componentStyle.isStatic,
    // but that'd be against the rules-of-hooks. We could be naughty and do it anyway as it
    // should be an immutable value, but behave for now.
    var theme = determineTheme(props, contextTheme, defaultProps) || EMPTY_OBJECT;
    var context = resolveContext(componentAttrs, props, theme);
    var elementToBeCreated = context.as || target;
    var propsForElement = {};
    for (var key in context) {
        if (context[key] === undefined) {
            // Omit undefined values from props passed to wrapped element.
            // This enables using .attrs() to remove props, for example.
        }
        else if (key[0] === '$' || key === 'as' || key === 'theme') {
            // Omit transient props and execution props.
        }
        else if (key === 'forwardedAs') {
            propsForElement.as = context.forwardedAs;
        }
        else if (!shouldForwardProp || shouldForwardProp(key, elementToBeCreated)) {
            propsForElement[key] = context[key];
            if (!shouldForwardProp &&
                process.env.NODE_ENV === 'development' &&
                !isPropValid(key) &&
                !seenUnknownProps.has(key) &&
                // Only warn on DOM Element.
                domElements.has(elementToBeCreated)) {
                seenUnknownProps.add(key);
                console.warn("styled-components: it looks like an unknown prop \"".concat(key, "\" is being sent through to the DOM, which will likely trigger a React console error. If you would like automatic filtering of unknown props, you can opt-into that behavior via `<StyleSheetManager shouldForwardProp={...}>` (connect an API like `@emotion/is-prop-valid`) or consider using transient props (`$` prefix for automatic filtering.)"));
            }
        }
    }
    var generatedClassName = useInjectedStyle(componentStyle, context);
    if (process.env.NODE_ENV !== 'production' && forwardedComponent.warnTooManyClasses) {
        forwardedComponent.warnTooManyClasses(generatedClassName);
    }
    var classString = joinStrings(foldedComponentIds, styledComponentId);
    if (generatedClassName) {
        classString += ' ' + generatedClassName;
    }
    if (context.className) {
        classString += ' ' + context.className;
    }
    propsForElement[
    // handle custom elements which React doesn't properly alias
    isTag(elementToBeCreated) &&
        !domElements.has(elementToBeCreated)
        ? 'class'
        : 'className'] = classString;
    propsForElement.ref = forwardedRef;
    return createElement(elementToBeCreated, propsForElement);
}
function createStyledComponent(target, options, rules) {
    var isTargetStyledComp = isStyledComponent(target);
    var styledComponentTarget = target;
    var isCompositeComponent = !isTag(target);
    var _a = options.attrs, attrs = _a === void 0 ? EMPTY_ARRAY : _a, _b = options.componentId, componentId = _b === void 0 ? generateId(options.displayName, options.parentComponentId) : _b, _c = options.displayName, displayName = _c === void 0 ? generateDisplayName(target) : _c;
    var styledComponentId = options.displayName && options.componentId
        ? "".concat(escape(options.displayName), "-").concat(options.componentId)
        : options.componentId || componentId;
    // fold the underlying StyledComponent attrs up (implicit extend)
    var finalAttrs = isTargetStyledComp && styledComponentTarget.attrs
        ? styledComponentTarget.attrs.concat(attrs).filter(Boolean)
        : attrs;
    var shouldForwardProp = options.shouldForwardProp;
    if (isTargetStyledComp && styledComponentTarget.shouldForwardProp) {
        var shouldForwardPropFn_1 = styledComponentTarget.shouldForwardProp;
        if (options.shouldForwardProp) {
            var passedShouldForwardPropFn_1 = options.shouldForwardProp;
            // compose nested shouldForwardProp calls
            shouldForwardProp = function (prop, elementToBeCreated) {
                return shouldForwardPropFn_1(prop, elementToBeCreated) &&
                    passedShouldForwardPropFn_1(prop, elementToBeCreated);
            };
        }
        else {
            shouldForwardProp = shouldForwardPropFn_1;
        }
    }
    var componentStyle = new ComponentStyle(rules, styledComponentId, isTargetStyledComp ? styledComponentTarget.componentStyle : undefined);
    function forwardRefRender(props, ref) {
        return useStyledComponentImpl(WrappedStyledComponent, props, ref);
    }
    if (process.env.NODE_ENV !== 'production') {
        forwardRefRender.displayName = displayName;
    }
    /**
     * forwardRef creates a new interim component, which we'll take advantage of
     * instead of extending ParentComponent to create _another_ interim class
     */
    var WrappedStyledComponent = React.forwardRef(forwardRefRender);
    WrappedStyledComponent.attrs = finalAttrs;
    WrappedStyledComponent.componentStyle = componentStyle;
    WrappedStyledComponent.shouldForwardProp = shouldForwardProp;
    if (process.env.NODE_ENV !== 'production') {
        WrappedStyledComponent.displayName = displayName;
    }
    // this static is used to preserve the cascade of static classes for component selector
    // purposes; this is especially important with usage of the css prop
    WrappedStyledComponent.foldedComponentIds = isTargetStyledComp
        ? joinStrings(styledComponentTarget.foldedComponentIds, styledComponentTarget.styledComponentId)
        : '';
    WrappedStyledComponent.styledComponentId = styledComponentId;
    // fold the underlying StyledComponent target up since we folded the styles
    WrappedStyledComponent.target = isTargetStyledComp ? styledComponentTarget.target : target;
    Object.defineProperty(WrappedStyledComponent, 'defaultProps', {
        get: function () {
            return this._foldedDefaultProps;
        },
        set: function (obj) {
            this._foldedDefaultProps = isTargetStyledComp
                ? merge({}, styledComponentTarget.defaultProps, obj)
                : obj;
        },
    });
    if (process.env.NODE_ENV !== 'production') {
        checkDynamicCreation(displayName, styledComponentId);
        WrappedStyledComponent.warnTooManyClasses = createWarnTooManyClasses(displayName, styledComponentId);
    }
    setToString(WrappedStyledComponent, function () { return ".".concat(WrappedStyledComponent.styledComponentId); });
    if (isCompositeComponent) {
        var compositeComponentTarget = target;
        hoist(WrappedStyledComponent, compositeComponentTarget, {
            // all SC-specific things should not be hoisted
            attrs: true,
            componentStyle: true,
            displayName: true,
            foldedComponentIds: true,
            shouldForwardProp: true,
            styledComponentId: true,
            target: true,
        });
    }
    return WrappedStyledComponent;
}
export default createStyledComponent;
//# sourceMappingURL=StyledComponent.js.map